import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skillsApi } from '../../utils/api';
import WizardLayout from '../../components/builder/WizardLayout';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import GlassCard from '../../components/GlassCard';
import ReactMarkdown from 'react-markdown';
import { 
  Download, Package, Eye, Beaker, CheckCircle, Check, Loader2, 
  Globe, Lock, Link2, FileText, Zap, BookOpen, GitBranch, Link, Lightbulb, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const Step4Download = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [skillInfo, setSkillInfo] = useState(null);
  const [visibility, setVisibility] = useState('PUBLIC');
  
  // Package data
  const [packageInfo, setPackageInfo] = useState(null);
  const [skillContent, setSkillContent] = useState(null);

  useEffect(() => {
    loadSkillPackage();
  }, [id]);

  const loadSkillPackage = async () => {
    try {
      const response = await skillsApi.get(id);
      const skill = response.data;
      setSkillInfo(skill);
      
      // Extract package info
      const slug = skill.slug || id;
      const packageMetadata = skill.package_metadata || {};
      const fileSizes = packageMetadata.file_sizes || {};
      
      setPackageInfo({
        slug: slug,
        zipUrl: `${process.env.REACT_APP_BACKEND_URL}/api/skills/${id}/download`,
        zipSize: fileSizes.total_package || 0,
        files: [
          { name: `${slug}.skill`, size: fileSizes.core || 0 },
          { name: `${slug}-prompt-library.md`, size: fileSizes.prompt_library || 0 },
          { name: `${slug}-usage-guide.md`, size: fileSizes.usage_guide || 0 },
          { name: `${slug}-decision-guide.md`, size: fileSizes.decision_guide || 0 }
        ]
      });
      
      // Extract skill content from bundled_resources
      const bundled = skill.bundled_resources || {};
      setSkillContent({
        overview: bundled.overview?.content || '# Overview\n\nNo content available.',
        promptLibrary: bundled.prompt_library?.content || '# Prompt Library\n\nNo content available.',
        usageGuide: bundled.usage_guide?.content || '# Usage Guide\n\nNo content available.',
        decisionGuide: bundled.decision_guide?.content || '# Decision Guide\n\nNo content available.',
        useCases: bundled.use_cases?.content || '# Use Cases\n\nNo content available.'
      });
    } catch (error) {
      console.error('Failed to load skill package', error);
      toast.error('Failed to load skill package');
    }
  };

  const handleDownloadPackage = () => {
    if (!packageInfo) return;
    
    const downloadLink = document.createElement('a');
    downloadLink.href = packageInfo.zipUrl;
    downloadLink.download = `${packageInfo.slug}.zip`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    
    toast.success('📥 Download started!');
  };

  const handlePublishAndDownload = async () => {
    setLoading(true);
    const progressToastId = toast.loading('Publishing skill...');
    
    try {
      await skillsApi.publish(id, visibility);
      toast.success('🎉 Skill published successfully!', { id: progressToastId });
      
      handleDownloadPackage();
      
      setTimeout(() => {
        navigate(`/skills/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Failed to publish', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to publish skill';
      toast.error(errorMessage, { id: progressToastId });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnother = () => {
    navigate('/builder/new');
  };

  const handleBack = () => {
    navigate(`/builder/${id}/step-3`);
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  if (!skillInfo || !packageInfo || !skillContent) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <WizardLayout currentStep={4} onBack={handleBack} showNext={false}>
      <div className="max-w-4xl mx-auto space-y-8 p-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
            Step 4 of 4: Download
          </h2>
          <p className="text-green-600 font-semibold">✓ Complete!</p>
        </div>

        {/* Success Banner with Two Buttons */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-16 w-16 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-green-900 dark:text-green-100">
                Your Skill Package is Ready!
              </h2>
              <p className="text-green-700 dark:text-green-300">
                {packageInfo.slug}.zip ({formatBytes(packageInfo.zipSize)})
              </p>
            </div>
          </div>

          {/* Two Primary Action Buttons */}
          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={handleDownloadPackage}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] text-white px-6 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all"
            >
              <Download className="h-6 w-6" />
              Download ZIP Package
            </Button>

            <Button
              onClick={handleCreateAnother}
              className="flex items-center justify-center gap-2 bg-white dark:bg-dark-bg-secondary border-2 border-[#e57f3f] text-[#e57f3f] px-6 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all"
            >
              <Plus className="h-6 w-6" />
              Create Another Skill
            </Button>
          </div>
        </div>

        {/* Package Contents */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Package className="h-6 w-6 text-[#e57f3f]" />
            Package Contents
          </h3>

          <div className="space-y-2">
            {packageInfo.files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <code className="text-sm font-mono text-gray-700 dark:text-gray-300">{file.name}</code>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatBytes(file.size)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between font-semibold text-gray-900 dark:text-gray-100">
            <span>Total Package Size:</span>
            <span className="text-[#e57f3f]">{formatBytes(packageInfo.zipSize)}</span>
          </div>
        </div>

        {/* Preview Section with Interactive Tabs */}
        {skillContent && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="h-6 w-6 text-[#e57f3f]" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Preview Your Skill Documentation
              </h2>
            </div>

            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                <TabsTrigger value="overview" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <FileText className="h-3 w-3" />
                  <span className="hidden sm:inline">Overview</span>
                </TabsTrigger>
                <TabsTrigger value="prompt-library" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <Zap className="h-3 w-3" />
                  <span className="hidden sm:inline">Prompts</span>
                </TabsTrigger>
                <TabsTrigger value="usage-guide" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <FileText className="h-3 w-3" />
                  <span className="hidden sm:inline">Usage</span>
                </TabsTrigger>
                <TabsTrigger value="decision-guide" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <GitBranch className="h-3 w-3" />
                  <span className="hidden sm:inline">Decision</span>
                </TabsTrigger>
                <TabsTrigger value="use-cases" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <Lightbulb className="h-3 w-3" />
                  <span className="hidden sm:inline">Use Cases</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.overview}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="prompt-library" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.promptLibrary}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="usage-guide" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.usageGuide}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="decision-guide" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.decisionGuide}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="use-cases" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.useCases}</ReactMarkdown>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Installation Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <Lightbulb className="h-6 w-6 text-blue-600" />
            Next Steps: Install Your Skill
          </h3>

          <ol className="space-y-3 mb-6 text-gray-700 dark:text-gray-300">
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">1.</span>
              <span>Download the ZIP package above</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">2.</span>
              <span>Go to <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">claude.ai</a> or open Claude desktop app</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">3.</span>
              <span>Navigate to: <strong>Settings → Capabilities → Skills → Upload .ZIP or .Skill</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">4.</span>
              <span>Test your skill with real inputs</span>
            </li>
          </ol>
        </div>

        {/* Visibility Settings */}
        <GlassCard className="p-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🌍 Visibility Settings
          </h2>
          <div className="space-y-3">
            {[
              { value: 'PRIVATE', label: 'Private (Only Me)', desc: 'Only you can see and use this skill', icon: Lock },
              { value: 'UNLISTED', label: 'Unlisted (Anyone with link)', desc: 'Not in public browse, but shareable', icon: Link2 },
              { value: 'PUBLIC', label: 'Public (Discoverable)', desc: 'Visible in Skills Browser', icon: Globe },
            ].map((option) => {
              const Icon = option.icon;
              return (
                <label 
                  key={option.value} 
                  className={`
                    flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all
                    ${visibility === option.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={option.value}
                    checked={visibility === option.value}
                    onChange={(e) => setVisibility(e.target.value)}
                    className="mt-1"
                  />
                  <Icon className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{option.label}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{option.desc}</p>
                  </div>
                </label>
              );
            })}
          </div>

          <Button
            onClick={handlePublishAndDownload}
            disabled={loading}
            className="w-full mt-6 bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all"
          >
            {loading ? 'Publishing...' : '📦 Publish & Download'}
          </Button>
        </GlassCard>
      </div>
    </WizardLayout>
  );
};

export default Step4Download;
